import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  IconButton,
  Divider,
  Box,
  Snackbar,
  Alert,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import CloseIcon from "@mui/icons-material/Close";
import { formFieldStyles, labelStyles, dialogTitleStyles, dialogDescriptionStyles } from "../../styles/formStyles";
import { getTransfersByKey, updateTransfer, getAvailableForFit } from "../../services/transfer";

interface MoveItemDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const MoveItemDialog = ({ open, onClose, onSuccess }: MoveItemDialogProps) => {
  const [moveData, setMoveData] = useState<any>({ itemId: "", buildingId: "", errors: {} });
  const [buildings, setBuildings] = useState<any[]>([]);
  const [itemDetails, setItemDetails] = useState<any>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false, message: "", severity: "success",
  });

  const [fetchingItem, setFetchingItem] = useState(false);
  const [saving, setSaving] = useState(false);

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!moveData.itemId) {
      setItemDetails(null);
      setBuildings([]);
      setMoveData((prev: any) => ({ ...prev, errors: {} }));
      return;
    }
    debounceRef.current = setTimeout(() => {
      fetchItemById(moveData.itemId);
    }, 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [moveData.itemId]);

  const fetchItemById = async (itemId: string) => {
    if (!itemId) {
      setItemDetails(null);
      setBuildings([]);
      return;
    }
    try {
      setFetchingItem(true);
      const transfer = await getTransfersByKey(itemId);
      if (!transfer) {
        setMoveData((prev: any) => ({ ...prev, errors: { itemId: "This ID does not exist" } }));
        setItemDetails(null);
        setBuildings([]);
        return;
      }
      if (transfer.transferStatus === "Transfered") {
        setMoveData((prev: any) => ({ ...prev, errors: { itemId: "This item is already transferred" } }));
        setItemDetails(null);
        setBuildings([]);
        return;
      }
      if (transfer.transferStatus !== "In_Transit") {
        setMoveData((prev: any) => ({ ...prev, errors: { itemId: "This item is not in In Transit status" } }));
        setItemDetails(null);
        setBuildings([]);
        return;
      }

      const inventory = transfer.inventory;
      setItemDetails({ ...inventory, transfer });
      setMoveData((prev: any) => ({ ...prev, errors: {} }));

      const res = await getAvailableForFit({ length: inventory.lengthCm, width: inventory.widthCm, height: inventory.heightCm, weight: inventory.itemWeight });
      setBuildings(Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : []);
    } catch {
      setMoveData((prev: any) => ({ ...prev, errors: { itemId: "Something went wrong" } }));
      setItemDetails(null);
      setBuildings([]);
    } finally {
      setFetchingItem(false);
    }
  };

  const handleSave = async () => {
    const errors: any = {};
    if (!moveData.itemId) errors.itemId = "SKU/Barcode is required";
    if (!moveData.buildingId) errors.buildingId = "Building selection is required";
    
    if (Object.keys(errors).length > 0) {
      setMoveData((prev: any) => ({ ...prev, errors }));
      return;
    }

    try {
      setSaving(true);
      await updateTransfer(moveData.itemId, {
        destinationBuildingId: Number(moveData.buildingId),
        transferStatus: "Picked",
        transferType: "Move",
      });
      setSnackbar({ open: true, message: "Item moved successfully", severity: "success" });
      setTimeout(() => {
        handleClose();
        onSuccess();
      }, 1500);
    } catch {
      setSnackbar({ open: true, message: "Failed to move item", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setMoveData({ itemId: "", buildingId: "", errors: {} });
    setBuildings([]);
    setItemDetails(null);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" PaperProps={{ sx: { borderRadius: "12px", width: "600px" } }} slotProps={{ backdrop: { sx: { backdropFilter: "blur(4px)" } } }}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Box>
            <Typography sx={dialogTitleStyles}>Move Item</Typography>
            <Typography sx={dialogDescriptionStyles}>Move item to a different building</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2, py: 1.5 }}>
          <Grid container spacing={1.5} sx={{ mt: 0 }}>
            <Grid item xs={6}>
              <Typography component="label" sx={labelStyles}>SKU / Barcode <span style={{ color: "red" }}>*</span></Typography>
              <TextField
                fullWidth size="small" placeholder="Enter SKU or Barcode"
                value={moveData.itemId}
                onChange={(e) => setMoveData((prev: any) => ({ ...prev, itemId: e.target.value }))}
                error={!!moveData.errors.itemId}
                helperText={moveData.errors.itemId}
                disabled={fetchingItem}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box component="img" src="/scan.svg" alt="scan" sx={{ width: 20, height: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={formFieldStyles}
              />
            </Grid>
            {fetchingItem && (
              <Grid item xs={6}>
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                  <CircularProgress size={24} />
                </Box>
              </Grid>
            )}
            {!fetchingItem && itemDetails && (
              <Grid item xs={6}>
                <Typography component="label" sx={labelStyles}>Building <span style={{ color: "red" }}>*</span></Typography>
                <TextField
                  select fullWidth size="small"
                  value={moveData.buildingId}
                  onChange={(e) => setMoveData((prev: any) => ({ ...prev, buildingId: e.target.value }))}
                  error={!!moveData.errors.buildingId}
                  helperText={moveData.errors.buildingId}
                  SelectProps={{ displayEmpty: true }}
                  sx={formFieldStyles}
                >
                  <MenuItem value="" disabled>Select Building</MenuItem>
                  {buildings.map((b: any) => (
                    <MenuItem key={b.id} value={String(b.id)}>{b.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2, py: 1.5, justifyContent: "space-between" }}>
          <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: "6px", border: "1px solid", borderColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset", padding: "8px 16px", textTransform: "none" }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || !moveData.buildingId} sx={{ borderRadius: "6px", border: "1px solid", borderColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset", padding: "8px 40px", textTransform: "none" }}>
            {saving ? <CircularProgress size={24} color="inherit" /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={2000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled"
          sx={{ width: "100%", backgroundColor: `${snackbar.severity === "success" ? "success.main" : "error.main"} !important`, color: "white", "& .MuiAlert-icon": { color: "white" }, "& .MuiAlert-action": { color: "white" } }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
export default MoveItemDialog;
